<template>
    <custom-modal :title="training ? training.title : 'Training Progress'" v-model="show" @close="close()" :loading="loading" :width="900">
        <div v-if="training" class="pa-2">
            <div v-if="training.provider" class="grey--text text--darken-1 mb-2">{{ training.provider }}</div>
            <div class="d-flex flex-wrap align-center mb-3">
                <v-progress-linear
                    :value="training.percent"
                    :color="training.percent == 100 ? 'green' : 'primary'"
                    height="8"
                    rounded
                    class="mr-3"
                    style="max-width: 300px;"
                ></v-progress-linear>
                <span class="text-no-wrap mr-3">
                    <strong>{{ training.done }}</strong> of {{ training.total }} completed ({{ training.percent }}%)
                </span>
                <span v-if="training.due_date" class="grey--text text--darken-1 text-no-wrap">
                    Due {{ $localeDate(training.due_date) }}
                </span>
            </div>

            <div class="d-flex flex-wrap align-center mb-2">
                <v-chip
                    v-for="f in filters"
                    :key="f.value"
                    small
                    label
                    class="mr-2 mb-2"
                    :color="filter == f.value ? f.color : undefined"
                    :text-color="filter == f.value ? 'white' : undefined"
                    @click="filter = f.value"
                >
                    {{ f.label }}: <strong class="ml-1">{{ f.count }}</strong>
                </v-chip>
                <v-spacer></v-spacer>
                <v-text-field
                    v-model="search"
                    dense
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    placeholder="search name or section..."
                    class="mb-2"
                    style="max-width: 260px;"
                ></v-text-field>
            </div>

            <v-simple-table dense fixed-header height="55vh">
                <template v-slot:default>
                    <thead>
                        <tr class="text-uppercase">
                            <th>Staff</th>
                            <th>Section</th>
                            <th>Status</th>
                            <th style="cursor: pointer; user-select: none;" @click="sortDesc = !sortDesc">
                                Completed On
                                <v-icon x-small>{{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(s, i) in visibleStaff" :key="i">
                            <td class="py-1">
                                <v-avatar size="28" class="mr-2">
                                    <img :src="s.profile_url" alt="" loading="lazy">
                                </v-avatar>
                                {{ s.name }}
                            </td>
                            <td class="caption">{{ s.section }}</td>
                            <td>
                                <v-chip pill label x-small :color="statusMeta(s.status).color" text-color="white" class="ma-0 px-1 text-uppercase">
                                    {{ statusMeta(s.status).label }}
                                </v-chip>
                            </td>
                            <td>{{ $localeDate(s.completed_on) || '—' }}</td>
                        </tr>
                        <tr v-if="!visibleStaff.length">
                            <td colspan="4" class="text-center grey--text py-6">No staff match this filter.</td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
            <div class="caption grey--text text-right mt-1">Showing {{ visibleStaff.length }} of {{ training.staff.length }}</div>
        </div>
    </custom-modal>
</template>

<script>
import CustomModal from '../util/CustomModal.vue'
import { statusMeta } from './trainingStatus'

// Read-only progress list for a training HR shared on the dashboard. Visible
// to all staff — the API returns names, sections, status and completion date
// only (no certificates), and reports rejected certificates as pending.
export default {
    components: { CustomModal },
    data: () => ({
        show: false,
        loading: false,
        training: null,
        filter: 'all',
        search: '',
        sortDesc: true,
    }),
    computed: {
        filters() {
            if (!this.training) return []
            const counts = this.training.status_counts
            return [
                { value: 'all', label: 'All', color: 'primary', count: this.training.staff.length },
                ...['verified', 'submitted', 'pending'].map(status => ({
                    value: status,
                    label: statusMeta(status).label,
                    color: statusMeta(status).color,
                    count: counts[status],
                })),
            ]
        },
        // API returns newest completion first, pending last. Ascending flips
        // only the completed rows; pending stays at the bottom either way.
        sortedStaff() {
            if (!this.training) return []
            const done = this.training.staff.filter(s => s.completed_on)
            const pending = this.training.staff.filter(s => !s.completed_on)
            if (this.sortDesc) return [...done, ...pending]
            const asc = [...done].sort((a, b) => a.completed_on.localeCompare(b.completed_on) || a.name.localeCompare(b.name))
            return [...asc, ...pending]
        },
        visibleStaff() {
            const term = (this.search || '').toLowerCase()
            return this.sortedStaff.filter(s => {
                if (this.filter != 'all' && s.status != this.filter) return false
                if (term) return s.name.toLowerCase().includes(term) || (s.section || '').toLowerCase().includes(term)
                return true
            })
        },
    },
    methods: {
        statusMeta,
        open(trainingId) {
            this.show = true
            this.loading = true
            this.$axios.get(`/api/staff-training/progress/${trainingId}`).then((resp) => {
                this.training = resp.data
            }).finally(() => this.loading = false)
        },
        close() {
            Object.assign(this.$data, this.$options.data())
        },
    },
}
</script>
